import { useEffect, useMemo, useRef, useState } from 'react';
import { getUserNotifications } from 'data/api';
import { transformUserNotificationDto } from 'data/transformers/generic-inbox-notification';
import { transformBrazeInboxCardNotificationDto } from 'data/transformers/braze';
import { UiState } from 'constants/index';
import { sortNotifications, sortPaginatedNotifications } from 'libs/utils/notification';
import useBrazeInboxNotificationCards from 'libs/common/braze/hooks/useBrazeInboxNotificationCards';
const NOTIFICATIONS_PER_PAGE_DROPDOWN = 5; // TODO: currently, we resolve success state after backend notifications are fetched
// meaning Braze notifications could come later on and cause flickering

const useFetchInboxNotifications = function useFetchInboxNotifications() {
  let {
    perPage = NOTIFICATIONS_PER_PAGE_DROPDOWN,
    isPaginated
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    0: uiState,
    1: setUiState
  } = useState(UiState.Idle);
  const {
    0: isFirstPageFetched,
    1: setIsFirstPageFetched
  } = useState(false);
  const {
    0: userNotifications,
    1: setUserNotifications
  } = useState([]);
  const {
    0: isEndReached,
    1: setIsEndReached
  } = useState(false);
  const currentPage = useRef(1);
  const {
    brazeNotificationCards,
    brazeControlNotificationCards
  } = useBrazeInboxNotificationCards();
  const brazeNotifications = useMemo(() => brazeNotificationCards ? brazeNotificationCards.map(card => transformBrazeInboxCardNotificationDto(card)) : [], [brazeNotificationCards]);
  const brazeNotificationCount = brazeNotifications.filter(notification => !notification.isViewed).length;
  const unsortedNotifications = [...userNotifications, ...brazeNotifications];
  const sortedNotifications = isPaginated ? sortPaginatedNotifications(unsortedNotifications, isEndReached) : sortNotifications(unsortedNotifications);
  useEffect(() => {
    const fetchUserNotifications = async () => {
      const response = await getUserNotifications({
        perPage,
        page: currentPage.current
      });

      if ('errors' in response) {
        setUiState(UiState.Failure);
        setIsEndReached(true);
        return;
      }

      if (!response.notifications.length) {
        setUiState(UiState.Success);
        setIsEndReached(true);
        setIsFirstPageFetched(true);
        return;
      }

      currentPage.current += 1;
      const newNotifications = response.notifications.map(transformUserNotificationDto);
      setUserNotifications(prevNotifications => [...prevNotifications, ...newNotifications]);
      setIsFirstPageFetched(true);
      setUiState(UiState.Success);
      setIsEndReached(response.notifications.length < perPage);
    };

    if (uiState === UiState.Pending) fetchUserNotifications();
  }, [uiState, perPage]);
  return {
    notifications: isFirstPageFetched ? sortedNotifications : [],
    setNotifications: setUserNotifications,
    controlNotificationCards: brazeControlNotificationCards,
    brazeNotificationCount,
    uiState,
    setUiState,
    isEndReached
  };
};

export default useFetchInboxNotifications;